import React, { Component } from "react"
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet-async';
import {
    Col,
    Container,
    Row,
    Spinner,
} from "reactstrap"
import { formatToShortHand } from "../../helpers/common_helper"
import BasicCountInfo from "./BasicCountInfo"
import MerchantsList from "../Stores/MerchantsList"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { withTranslation } from "react-i18next"
import { ToastContainer } from "react-toastify";

// Utilities
import {
    createNotificationHelpers,
    loadDashboardData,
    transformInstalledStoresToTableData,
    DASHBOARD_INSTALLED_STORES_COLUMNS,
    MESSAGES
} from "../utils";

class Dashboard extends Component {
    constructor(props) {
        super(props)
        this.state = {
            revenue: 0,
            totalStores: 0,
            MRR: 0,
            ARR: 0,
            legacyPlan: 0,
            starterPlan: 0,
            growthPlan: 0,
            advancedPlan: 0,
            premiumPlan: 0,
            enterprisePlan: 0,
            legacyPlanPrice: 0,
            growthPlanPrice: 0,
            starterPlanPrice: 0,
            premiumPlanPrice: 0,
            advancedPlanPrice: 0,
            enterprisePlanPrice: 0,
            totalHeadlessStores: 0,
            totalBasicStores: 0,
            totalShopifyPlusStores: 0,
            loading: false,
            lastInstalledStores: [],
            installedStores: 0,
        }

        // Initialize notification helpers
        const notifications = createNotificationHelpers();
        this.notify_info = notifications.notify_info;
        this.notify_error = notifications.notify_error;
        this.notify_success = notifications.notify_success;
    }

    /**
     * Loads dashboard data and updates state
     */
    loadDashboard = async () => {
        const data = await loadDashboardData(this.notify_error, 20);

        if (data) {
            // Transform installed stores if available
            const table_records = data.lastInstalledStores?.length > 0
                ? transformInstalledStoresToTableData(
                    data.lastInstalledStores,
                    DASHBOARD_INSTALLED_STORES_COLUMNS
                )
                : [];

            // Combine all state updates in a single setState call
            this.setState({
                ...data.dashboard,
                loading: true,
                installedStores: data.lastInstalledStores?.length || 0,
                lastInstalledStores: table_records,
            });

            this.notify_success(MESSAGES.DASHBOARD_LOADED_SUCCESS);
        }
    }

    /**
     * Renders a row of BasicCountInfo cards
     */
    renderCountInfoRow = (items) => {
        return (
            <Row>
                {items.map((item, index) => (
                    <BasicCountInfo key={index} {...item} />
                ))}
            </Row>
        );
    }

    /**
     * Renders the loading spinner
     */
    renderLoadingSpinner = () => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
            <Spinner color="primary" />
            <span style={{ marginLeft: '10px' }}>Loading ...</span>
        </div>
    )

    componentDidMount() {
        this.loadDashboard();
    }

    render() {
        const {
            revenue = 0,
            totalStores = 0,
            MRR = 0,
            ARR = 0,
            legacyPlan = 0,
            starterPlan = 0,
            growthPlan = 0,
            advancedPlan = 0,
            premiumPlan = 0,
            enterprisePlan = 0,
            legacyPlanPrice = 0,
            growthPlanPrice = 0,
            starterPlanPrice = 0,
            premiumPlanPrice = 0,
            advancedPlanPrice = 0,
            enterprisePlanPrice = 0,
            totalHeadlessStores = 0,
            lastInstalledStores,
            installedStores,
            totalBasicStores,
            totalShopifyPlusStores
        } = this.state;
        const { t } = this.props;

        return (
            <React.Fragment>
                <div className="page-content">
                    <Helmet>
                        <title>Veeper | AP - Home</title>
                    </Helmet>
                    <Container fluid>
                        <Breadcrumbs
                            title={t("Home")}
                            breadcrumbItem={t("How We're doing")}
                        />
                        <ToastContainer />
                        {this.state.loading ? (
                            <React.Fragment>
                                {this.renderCountInfoRow([
                                    { store_domain: "", colSize: "3", data: `$${formatToShortHand(MRR)}`, title: "MRR" },
                                    { store_domain: "", colSize: "3", data: `$${formatToShortHand(ARR)}`, title: "ARR" },
                                    { store_domain: "", colSize: "3", data: totalStores, linkUrl: "stores", title: "Clients" },
                                    { store_domain: "", colSize: "3", data: `$${formatToShortHand(revenue)}`, title: "Revenue" }
                                ])}
                                {this.renderCountInfoRow([
                                    { store_domain: "", colSize: "3", data: totalBasicStores, secondParams: 'yes', title: "Basic Stores", isClick: true },
                                    { store_domain: "", colSize: "3", data: totalHeadlessStores, secondParams: 'yes', title: "Headless Stores", isClick: true },
                                    { store_domain: "", colSize: "3", data: totalShopifyPlusStores, secondParams: 'yes', title: "Shopify Plus Stores", isClick: true }
                                ])}
                                {this.renderCountInfoRow([
                                    { store_domain: "", colSize: "3", data: legacyPlan, price: formatToShortHand(legacyPlanPrice), secondParams: 'yes', title: "Legacy Plan", isClick: true },
                                    { store_domain: "", colSize: "3", data: starterPlan, price: formatToShortHand(starterPlanPrice), secondParams: 'yes', title: "Starter Plan", isClick: true },
                                    { store_domain: "", colSize: "3", data: growthPlan, price: formatToShortHand(growthPlanPrice), secondParams: 'yes', title: "Growth Plan", isClick: true }
                                ])}
                                {this.renderCountInfoRow([
                                    { store_domain: "", colSize: "3", data: advancedPlan, price: formatToShortHand(advancedPlanPrice), secondParams: 'yes', title: "Advanced Plan", isClick: true },
                                    { store_domain: "", colSize: "3", data: premiumPlan, price: formatToShortHand(premiumPlanPrice), secondParams: 'yes', title: "Premium Plan", isClick: true },
                                    { store_domain: "", colSize: "3", data: enterprisePlan, price: formatToShortHand(enterprisePlanPrice), secondParams: 'yes', title: "Enterprise Plan", isClick: true }
                                ])}
                                <Row>
                                    <Col xl={12}>
                                        <MerchantsList
                                            data={lastInstalledStores}
                                            default_entries={20}
                                            displayEntries={false}
                                            searching={false}
                                            title={` Last ${installedStores} Installed Stores`}
                                            paging={false}
                                        />
                                    </Col>
                                </Row>
                            </React.Fragment>
                        ) : (
                            this.renderLoadingSpinner()
                        )}

                    </Container>
                </div>

            </React.Fragment>
        )

    }
}

Dashboard.propTypes = {
    t: PropTypes.any
}

export default withTranslation()(Dashboard)
